// pages/Objectives.js
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const objectives = [
  { icon: "🤝", text: "Promote economic & social development of members through self-help and cooperation." },
  { icon: "🌱", text: "Provide timely, affordable access to quality agri-inputs: seeds, fertilizers, tools & services." },
  { icon: "🏭", text: "Create infrastructure for storage, processing, and value-addition of farm produce." },
  { icon: "💳", text: "Enable access to low-interest credit, insurance, and investment support for farmers." },
  { icon: "🐄", text: "Support allied rural ventures: dairy, poultry, fisheries, beekeeping, compost units." },
  { icon: "🧑‍💻", text: "Conduct training, cooperative awareness & digital literacy for youth and members." },
  { icon: "🏛️", text: "Leverage Government schemes & institutional partnerships for funding and capacity building." },
  { icon: "🚚", text: "Build cold-chains, logistics, solar irrigation & rural marts for market-driven services." },
  { icon: "🔎", text: "Ensure transparency via digitized accounting, audits & participatory governance." },
];

export default function Objectives() {
  return (
    <div className="relative min-h-screen bg-[var(--page-bg)] overflow-hidden">
      {/* Soft accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-20 bg-[radial-gradient(circle_at_center,rgba(14,98,173,.25),transparent_60%)]" />
        <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-20 bg-[radial-gradient(circle_at_center,rgba(30,138,61,.25),transparent_58%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 md:py-14">
        {/* Breadcrumb + Title */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <nav className="text-xs md:text-sm text-neutral-500 flex gap-2 md:gap-3">
            <Link to="/" className="hover:text-[var(--gov-blue)]">Home</Link>
            <span>/</span>
            <span>About Society</span>
            <span>/</span>
            <span className="text-[var(--gov-blue)] font-semibold">Objectives</span>
          </nav>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight text-[var(--gov-navy)]">
            🎯 Our Society’s Core Objectives
          </h1>
          <p className="mt-2 text-sm md:text-base text-neutral-600 max-w-2xl">
            Empowering communities through inclusive growth, technology, and cooperation.
          </p>
        </motion.div>

        {/* Intro + Objectives Grid */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-8 rounded-2xl border border-neutral-200 bg-white/90 backdrop-blur-md shadow-[0_12px_40px_rgba(0,0,0,0.08)] overflow-hidden"
        >
          <div className="h-1.5 w-full bg-gradient-to-r from-[var(--gov-blue)] via-[var(--gov-green)] to-[var(--gov-blue-deep)]" />
          <div className="p-6 md:p-10">
            <p className="text-[15px] md:text-lg leading-relaxed text-neutral-800">
              Shine Agri Allied & Marketing Multistate Cooperative Society Ltd. is dedicated to transforming the
              agricultural landscape and strengthening rural livelihoods through strategic, cooperative-led initiatives.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {objectives.map((o, i) => (
                <ObjectiveCard key={i} icon={o.icon} text={o.text} index={i} />
              ))}
            </div>
          </div>
        </motion.section>

        {/* Pillars / Focus Areas */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-8"
        >
          <h3 className="text-xl md:text-2xl font-extrabold text-[var(--gov-navy)] mb-4">Strategic Focus Areas</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { tag: "Transparency", color: "from-[var(--gov-blue)] to-[var(--gov-blue-deep)]", text: "Open registers, digital workflows & audits." },
              { tag: "Sustainability", color: "from-[var(--gov-green)] to-[var(--gov-green-dark)]", text: "Climate-smart practices & water stewardship." },
              { tag: "Logistics", color: "from-amber-600 to-orange-700", text: "Storage, cold-chain, transport & export readiness." },
              { tag: "Inclusivity", color: "from-sky-600 to-sky-700", text: "Women, SC/ST & youth leadership in cooperatives." },
            ].map((p, i) => (
              <motion.div
                key={p.tag}
                variants={fadeUp}
                className="rounded-xl border border-neutral-200 bg-white shadow hover:shadow-md transition overflow-hidden"
              >
                <div className={`h-1.5 bg-gradient-to-r ${p.color}`} />
                <div className="p-4">
                  <div className="font-bold text-[var(--gov-navy)]">{p.tag}</div>
                  <p className="text-sm text-neutral-700 mt-1">{p.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-10"
        >
          <div className="rounded-2xl p-5 md:p-7 bg-gradient-to-r from-[var(--gov-green)] via-emerald-600 to-[var(--gov-blue)]
                          text-white shadow-[0_12px_40px_rgba(0,0,0,0.12)] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xl md:text-2xl font-extrabold">Ready to support our objectives?</div>
              <p className="text-white/90 text-sm md:text-base">Become a member or collaborate with us on storage, logistics & export.</p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/apply"
                className="px-4 py-2 rounded-full bg-white text-[var(--gov-navy)] font-semibold shadow hover:opacity-95"
              >
                Apply Membership
              </Link>
              <Link
                to="/contact/HeadOffice"
                className="px-4 py-2 rounded-full border border-white/70 font-semibold hover:bg-white/10"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
}

function ObjectiveCard({ icon = "✅", text, index = 0 }) {
  return (
    <motion.div
      variants={fadeUp}
      className="group rounded-xl border border-neutral-200 bg-white p-5 shadow hover:shadow-md transition"
    >
      <div className="flex items-start gap-3">
        <span className="text-xl select-none">{icon}</span>
        <p className="text-[var(--gov-navy)]/90 text-[15px] leading-relaxed">
          {text}
        </p>
      </div>
      <div className="mt-3 h-1 rounded bg-gradient-to-r from-[var(--gov-blue)]/20 to-[var(--gov-green)]/20 group-hover:from-[var(--gov-blue)]/40 group-hover:to-[var(--gov-green)]/40 transition" />
    </motion.div>
  );
}
